import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Calendar, MapPin, Users, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import VerifiedBadge from './VerifiedBadge';
import { assetUrl, getMyRides } from '../apis/ride-24';

const FALLBACK_COVERS = [
  'https://images.unsplash.com/photo-1620193565717-cdc48028fc56?w=1200&q=80',
  'https://images.unsplash.com/photo-1597840662268-226a09bc49f7?w=1200&q=80',
  'https://images.unsplash.com/photo-1466095834737-14d0fe2abe94?w=1200&q=80',
];

const hashCode = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MyRidesPage = ({ user }) => {
  const [tab, setTab] = useState('going');
  const [going, setGoing] = useState([]);
  const [organised, setOrganised] = useState([]);
  const [loading, setLoading] = useState(true);

  const canOrganise = user.is_verified || user.organiser_status === 'approved';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [getMyRides({ type: 'going' })];
      if (canOrganise) {
        requests.push(getMyRides({ type: 'organised' }));
      }
      const results = await Promise.all(requests);
      setGoing(results[0].data);
      if (canOrganise) setOrganised(results[1].data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load your rides');
    } finally {
      setLoading(false);
    }
  }, [canOrganise]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-red-500 font-heading mb-2">
            Your saddle
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-none">
            My rides
          </h1>
          <p className="text-zinc-400 mt-3">
            Everything you&apos;re signed up for and everything you&apos;ve organised.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <TabsTrigger
              value="going"
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-heading uppercase tracking-widest text-xs"
              data-testid="my-rides-tab-going"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Going ({going.length})
            </TabsTrigger>
            {canOrganise && (
              <TabsTrigger
                value="organised"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-heading uppercase tracking-widest text-xs"
                data-testid="my-rides-tab-organised"
              >
                <ShieldCheck className="w-4 h-4 mr-1.5" />
                Organised ({organised.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="going" className="mt-6" data-testid="my-rides-going-panel">
            <RidesList
              rides={going}
              loading={loading}
              emptyTitle="You haven&apos;t RSVP'd to any rides yet"
              emptyBody="Head to the dashboard and tap 'I'm Going' on a ride you like."
              emptyCta={{ to: '/ride-24', label: 'Browse rides' }}
            />
          </TabsContent>

          {canOrganise && (
            <TabsContent value="organised" className="mt-6" data-testid="my-rides-organised-panel">
              <RidesList
                rides={organised}
                loading={loading}
                emptyTitle="You haven&apos;t organised any rides yet"
                emptyBody="Create your first ride and gather your riders."
                emptyCta={{ to: '/ride-24/create-event', label: 'Create ride' }}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

const RidesList = ({ rides, loading, emptyTitle, emptyBody, emptyCta }) => {
  if (loading) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-72 bg-zinc-900 rounded-sm animate-pulse" />
        ))}
      </div>
    );
  }

  if (!rides || rides.length === 0) {
    return (
      <Card
        className="text-center py-14 bg-zinc-950 border border-zinc-900 text-zinc-200"
        data-testid="my-rides-empty"
      >
        <CardContent>
          <i className="fa-solid fa-motorcycle text-red-500 text-5xl mb-3 block" aria-hidden="true"></i>
          <h3 className="font-heading text-xl uppercase tracking-tight text-white mb-2">
            {emptyTitle}
          </h3>
          <p className="text-zinc-500 mb-4">{emptyBody}</p>
          {emptyCta && (
            <Link to={emptyCta.to}>
              <Button className="btn-hover bg-red-600 hover:bg-red-700 text-white border-0 rounded-sm font-heading uppercase tracking-widest">
                <Plus className="w-4 h-4 mr-2" />
                {emptyCta.label}
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {rides.map((event) => (
        <RideCard key={event.id} event={event} />
      ))}
    </div>
  );
};

const RideCard = ({ event }) => {
  const cover =
    event.photos && event.photos.length > 0
      ? assetUrl(event.photos[0].path)
      : FALLBACK_COVERS[hashCode(event.id) % FALLBACK_COVERS.length];

  return (
    <Link
      to={`/ride-24/events/${event.id}`}
      className="group block"
      data-testid={`my-ride-card-${event.id}`}
    >
      <article className="relative overflow-hidden rounded-sm border border-zinc-900 bg-zinc-950 hover:border-red-900/60 hover:ring-1 hover:ring-red-900/40 transition-all">
        <div className="relative h-56 overflow-hidden">
          <img
            src={cover}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="mb-1.5 text-xs uppercase tracking-widest text-red-400 font-heading">
              <Calendar className="inline w-3 h-3 mr-1 -mt-0.5" />
              {formatDate(event.date)}
            </div>
            <h3 className="font-heading text-xl sm:text-2xl uppercase tracking-tight text-white line-clamp-2">
              {event.title}
            </h3>
          </div>
        </div>

        <div className="p-4 flex items-center justify-between text-xs text-zinc-400 border-t border-zinc-900">
          <span className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-red-500" />
            {[event.district, event.state].filter(Boolean).join(', ') || event.start_point}
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            <span className="text-zinc-200 font-medium">{event.attending_count || 0}</span>
            / {event.max_participants}
            {event.organizer_is_verified && <VerifiedBadge size="sm" />}
          </span>
        </div>
      </article>
    </Link>
  );
};

export default MyRidesPage;
